/**
 * nj-binding — the three declarations Stage 1 of the Events-in-New-Journeys
 * build plan calls for. No behavior lands here (stage 2 wires the record,
 * stage 3 builds the Setup views) — this is the schema every later stage
 * builds against, so no control or page type is ever added without an owner.
 */

import { REG, CORE_CFG, ATTACH_FOR, ATTACH_TYPES, docFromEvent, type Doc, type EventLike, type GuestEntry, type QueueConfigLike, type BoardConfigLike } from "./nj-model";
import { requiredEventChecks, scheduleEndState, type RequiredCheck } from "../events/creation-store";
import { refetchEvents } from "../events/useEvents";
import { readResolvesHolds } from "../../lib/holds";
import { toParts } from "../../lib/datetime";
import { creation, publishEvent, takeEventOffline, getStudioEvent, getEventShare } from "@v0y4ge/api-client";
import {
  updateQueue, setQueueStatus, saveMessageBoard, deleteMessageBoard,
  type QueueStatus, type MessageBoard,
} from "../queues/queues-api";

/** `getStudioEvent` takes the request options every admin read in this app passes
 *  (kit 174e98dd made them required). Same base resolution as the shell. */
const EV_OPTS = { baseUrl: (window as unknown as { __STUDIO_API_BASE__?: string }).__STUDIO_API_BASE__ ?? "" };

/** Re-exported so the screen can import the guest shape from the binding it
 *  already imports the guest writers from. */
export type { GuestEntry } from "./nj-model";

/* ── 1. Field ownership map ──────────────────────────────────────────
 * Every control the designer will show writes to exactly one store, through
 * exactly one op. `scope` is where the control lives in the rail/canvas/panel
 * model; `store` + `op` is who owns the write.
 */
export type FieldScope =
  | "setup.basics" | "setup.details" | "setup.schedule" | "setup.tickets" | "setup.guests" | "setup.messaging"
  /** Queue setup sections (queue editor port, 2026-08-24). */
  | "setup.type" | "setup.hours" | "setup.staffing" | "setup.resources" | "setup.services"
  /** Display-board setup. */
  | "setup.config"
  /** Appointment setup sections (2026-09-08). These three were being USED as
   *  scopes by rows in the table below while the union did not list them, so
   *  every one of those rows was a type error under the kit's strict config and
   *  `ownerOf()` could return a scope no consumer had a case for. */
  | "setup.places" | "setup.window" | "setup.perservice"
  /** Applies across every Setup section, not one of them. */
  | "setup.*"
  | "publish" | "page" | "block";
export type FieldStore = "record" | "doc" | "derived";

export interface FieldOwnership {
  /** Human-readable label for the map. NEVER matched against — see `keys`. */
  field: string;
  /** The real record/document field names this row owns. `ownerOf()` matches
   *  these, so a row with a compound or annotated label stays reachable. */
  keys: string[];
  scope: FieldScope;
  store: FieldStore;
  op: string;
  note?: string;
}

/** MIRROR WARNING: this array and the ownership table in
 *  "Events in New Journeys - Build Plan.dc.html" are two copies of the SAME
 *  declaration. They have drifted before. Change both, or neither. */
export const EVENT_FIELD_OWNERSHIP: FieldOwnership[] = [
  { field: "title", keys: ["title"], scope: "setup.basics", store: "record", op: "updateEvent" },
  { field: "description", keys: ["description"], scope: "setup.basics", store: "record", op: "updateEvent" },
  { field: "tags", keys: ["tags"], scope: "setup.basics", store: "record", op: "updateEvent" },
  { field: "heroImage", keys: ["heroImage"], scope: "setup.basics", store: "record", op: "updateEvent", note: "URL field or an image dropped on the card preview" },
  { field: "method (format)", keys: ["method"], scope: "setup.details", store: "record", op: "updateEvent" },
  { field: "visibility", keys: ["visibility"], scope: "setup.details", store: "record", op: "updateEvent" },
  { field: "location / venue / address", keys: ["location", "venueName", "address", "locationId", "locationMode"], scope: "setup.details", store: "record", op: "updateEvent" },
  { field: "virtual provider / join link", keys: ["virtualProvider"], scope: "setup.details", store: "record", op: "updateEvent" },
  { field: "registration window", keys: ["registrationOpens", "registrationCloses"], scope: "setup.details", store: "record", op: "updateEventRegistration", note: "MOVED here from the register page; the single registrationWindow key is deleted" },
  { field: "consent and marketing, terms copy", keys: ["consent", "termsCopy"], scope: "setup.details", store: "record", op: "updateEventRegistration", note: "MOVED here from the register page" },
  { field: "after-registration redirect", keys: ["redirectUrl"], scope: "setup.details", store: "record", op: "updateEventRegistration", note: "MOVED here from the register page" },
  { field: "start / end / timezone / all-day", keys: ["start", "end", "timezone", "allDay"], scope: "setup.schedule", store: "record", op: "updateEvent" },
  { field: "recurrence", keys: ["repeatIndex"], scope: "setup.schedule", store: "record", op: "updateEvent" },
  { field: "custom recurrence dates", keys: ["customDates"], scope: "setup.schedule", store: "record", op: "updateEvent", note: "only when repeatIndex is the Custom entry" },
  { field: "per-day hours", keys: ["dayHours"], scope: "setup.schedule", store: "record", op: "updateEvent", note: "multi-day events only" },
  { field: "selected ticket types", keys: ["ticketTypeIds"], scope: "setup.tickets", store: "record", op: "updateEvent", note: "selected from the library, never created here" },
  { field: "per-type allocation", keys: ["ticketTypeConfigs"], scope: "setup.tickets", store: "record", op: "updateEvent" },
  { field: "total tickets available", keys: ["capacity"], scope: "setup.tickets", store: "record", op: "updateEvent", note: "entered directly, then allocated across types; partial allocation is legal" },
  { field: "waitlist", keys: ["waitlist"], scope: "setup.tickets", store: "record", op: "updateEvent", note: "moved off the register page, stage 0" },
  { field: "selected ticket groups", keys: ["ticketGroupIds"], scope: "setup.tickets", store: "record", op: "applyTicketGroupToEvent", note: "Tickets v2 (CC 0195): applied to an existing event at once, to a new one at Publish; doc attach.ticketGroupId is a MIRROR of the first selection" },
  { field: "guest list / segments", keys: ["guestList"], scope: "setup.guests", store: "record", op: "updateEventGuestList" },
  { field: "message flow selection", keys: ["messagingFlowId"], scope: "setup.messaging", store: "record", op: "updateEventMessaging", note: "selects which library flow runs; flow authoring itself is the Flow view" },
  { field: "registration fields that stayed page-scope", keys: ["approvalMode", "maxGuests", "standardFields", "promoId"], scope: "page", store: "record", op: "updateEventRegistration" },
  { field: "question group reference", keys: ["attach.groupId"], scope: "page", store: "doc", op: "putJourneyDocument" },
  { field: "booking questions", keys: ["bookingQuestions"], scope: "setup.basics", store: "record", op: "createEvent | updateEvent", note: "the attached group as [{ kind: 'group', id }], plus any direct question refs already stored; sent on create, and on a live event only when it changed (CC 0030 item 17, JAI-1946)" },
  { field: "survey reference", keys: ["surveys"], scope: "setup.messaging", store: "record", op: "updateEventMessaging", note: "moved off the journey document 2026-09-02 — an event's survey is the event's, so two journeys against one event cannot disagree about it" },
  { field: "coupons accepted", keys: ["coupons"], scope: "setup.tickets", store: "record", op: "updateEvent", note: "event-scoped, flat (not per method) — a coupon discounts a price, and an event has one way in" },
  { field: "promo reference", keys: ["attach.promoId"], scope: "page", store: "doc", op: "putJourneyDocument" },
  { field: "page text", keys: ["pages[].h", "pages[].s"], scope: "page", store: "doc", op: "putJourneyDocument" },
  { field: "core settings", keys: ["pages[].toggles"], scope: "page", store: "doc", op: "putJourneyDocument" },
  { field: "blocks", keys: ["pages[].content"], scope: "block", store: "doc", op: "putJourneyDocument" },
  { field: "flow graph", keys: ["flow"], scope: "page", store: "doc", op: "putJourneyDocument | saveBuilderEventFlow", note: "one tree since 2026-08-31 — replaced flowMain/flowYes/flowNo" },
  { field: "entry page", keys: ["entryUid"], scope: "publish", store: "doc", op: "putJourneyDocument", note: "journey-level, stage 1. NOT surfaced in any Setup section yet" },
  { field: "publishing settings", keys: ["publish.slug", "publish.tenantHost", "publish.seoTitle", "publish.shareImage"], scope: "publish", store: "doc", op: "putJourneyDocument", note: "NOT surfaced in any Setup section yet" },
  { field: "appearance", keys: ["publish.theme", "publish.font", "publish.customCss"], scope: "publish", store: "doc", op: "putJourneyDocument", note: "NOT surfaced in any Setup section yet" },
  { field: "published page bundle", keys: ["publishedBundle"], scope: "publish", store: "derived", op: "saveEventLandingDraft | publishEvent", note: "not user-editable. publishEvent POSTs /v1/landing/{id}/publish, so publishing does hit the landing surface; what is still missing is the document → landing-envelope draft write (saveEventLandingDraft). publishEventLanding does not exist yet — Claude Code confirmed 2026-08-22 they will add it" },
  { field: "ANY setup field, before an owning event exists", keys: ["setup"], scope: "setup.*", store: "doc", op: "putJourneyDocument setup{} (debounced)", note: "holding store: a template-created (local:) journey has no record to write to, and no attach step exists, so values stay in Doc.setup" },
];

/** Enforce "no control without an owner" — matches the row's real field
 *  `keys`, never its display label, so annotated rows stay reachable. */
export function ownerOf(field: string): FieldOwnership | undefined {
  return EVENT_FIELD_OWNERSHIP.find((f) => f.keys.includes(field));
}

/* ── 2. Inspector schema per page type ────────────────────────────────
 * Composed from what the registry already declares (REG's core + toggles,
 * CORE_CFG, ATTACH_FOR) rather than duplicated — this is the formal shape the
 * panel renders from, so a page type with no declaration shows nothing extra.
 */
export interface PageInspectorSchema {
  pageType: string;
  hasPageText: true;
  hasCore: boolean;
  coreSettings: string[];       // keys from CORE_CFG[pageType]
  attachment: { type: string; libraryTitle: string; route: string } | null;
  blocksAllowed: "all" | "all-minus-hero";
}

export function inspectorSchemaFor(pageType: string): PageInspectorSchema {
  const reg = REG.find((r) => r.id === pageType);
  const attachType = ATTACH_FOR[pageType];
  const attach = attachType
    ? { type: attachType, libraryTitle: ATTACH_TYPES[attachType] ?? attachType, route: attachType }
    : null;
  return {
    pageType,
    hasPageText: true,
    hasCore: !!reg?.core,
    coreSettings: (CORE_CFG[pageType] ?? []).map((row) => row[0]),
    attachment: attach,
    blocksAllowed: reg?.core ? "all-minus-hero" : "all",
  };
}

/* ── 3. Object binding per journey type ───────────────────────────────
 * What a journey of this type writes to, besides the document. Queues have
 * had this implicitly since the write-back landed; naming it here is what
 * makes appointments and experiences a configuration instead of a rebuild.
 */
export interface JourneyBinding {
  type: string;
  /** false = no owning object; the document is the only store (experiences). */
  ownsRecord: boolean;
  ops?: {
    read: string;
    update: string;
    publish?: string;
    takeOffline?: string;
    cancel?: string;
    discard?: string;
  };
  /** Name of the nj-model function that derives a Doc from the real object. */
  deriveDocFn?: string;
  /** Name of the function (screens/events/creation-store.ts today) that returns
   *  the readiness rows the Publish view and the rail's dots render from. */
  requiredChecksFn?: string;
}

export const EVENT_BINDING: JourneyBinding = {
  type: "Event",
  ownsRecord: true,
  ops: {
    read: "getStudioEvent",
    update: "creation.updateEvent",
    publish: "publishEvent",
    takeOffline: "takeEventOffline",
    cancel: "creation.cancelEvent",
    discard: "creation.discardEvent",
  },
  deriveDocFn: "docFromEvent",
  requiredChecksFn: "requiredEventChecks",
};

export const QUEUE_BINDING: JourneyBinding = {
  type: "Queue",
  ownsRecord: true,
  ops: { read: "listQueues", update: "updateQueue", publish: "setQueueStatus(open)", takeOffline: "setQueueStatus(closed)" },
  deriveDocFn: "docFromQueue",
  requiredChecksFn: "queueReadiness",
};

export const BOARD_BINDING: JourneyBinding = {
  type: "Board",
  ownsRecord: true,
  ops: { read: "listMessageBoards", update: "saveMessageBoard", discard: "deleteMessageBoard" },
  deriveDocFn: "docFromBoard",
  requiredChecksFn: "boardReadiness",
};

/* ── Queue field ownership — the queue editor's every control, ported ──
 * Same contract as the event map: one control, one store, one op. The queue
 * editor wrote all of these through updateQueue (autosaved) and published
 * through setQueueStatus; moving the surface into the designer does not move
 * the writes. */
export const QUEUE_FIELD_OWNERSHIP: FieldOwnership[] = [
  { field: "queue name", keys: ["name"], scope: "setup.type", store: "record", op: "updateQueue" },
  { field: "queue type", keys: ["typeId"], scope: "setup.type", store: "record", op: "updateQueue", note: "picking a type also seeds the journey and the type's wait-target preset" },
  { field: "location", keys: ["locationId", "locationName"], scope: "setup.type", store: "record", op: "createQueue", note: "set at create; a queue is owned by its location's branch company, so it can't move afterwards" },
  { field: "ticket prefix", keys: ["ticketPrefix"], scope: "setup.type", store: "record", op: "updateQueue" },
  { field: "wait target", keys: ["slaTargetMins"], scope: "setup.type", store: "record", op: "updateQueue" },
  { field: "hero image", keys: ["heroImage"], scope: "setup.type", store: "record", op: "updateQueue", note: "tops every page of the queue journey" },
  { field: "journey seed source", keys: ["journeySource"], scope: "setup.type", store: "record", op: "updateQueue" },
  { field: "opening hours", keys: ["hours"], scope: "setup.hours", store: "record", op: "updateQueue" },
  { field: "assigned staff", keys: ["assignedStaff"], scope: "setup.staffing", store: "record", op: "updateQueue" },
  { field: "positions needed", keys: ["needed"], scope: "setup.staffing", store: "record", op: "updateQueue" },
  { field: "resources", keys: ["resources"], scope: "setup.resources", store: "record", op: "updateQueue" },
  { field: "ordered service selection", keys: ["serviceSelection", "services"], scope: "setup.services", store: "record", op: "updateQueue", note: "categories resolve to their catalogue services live; `services` is the resolved flat list" },
  { field: "message flow selection", keys: ["messagingFlowId"], scope: "setup.messaging", store: "record", op: "updateQueue" },
  { field: "open / paused / closed", keys: ["status"], scope: "publish", store: "record", op: "setQueueStatus" },
  { field: "pages, blocks and flow", keys: ["pages", "flow"], scope: "page", store: "doc", op: "putJourneyDocument" },
  { field: "ANY queue setup field, before a queue record exists", keys: ["qsetup"], scope: "setup.*", store: "doc", op: "putJourneyDocument qsetup{} (debounced)" },
];

/* ── Display-board field ownership ─────────────────────────────────── */
export const BOARD_FIELD_OWNERSHIP: FieldOwnership[] = [
  { field: "board name", keys: ["name"], scope: "setup.config", store: "record", op: "saveMessageBoard" },
  { field: "queues shown", keys: ["queueIds"], scope: "setup.config", store: "record", op: "saveMessageBoard" },
  { field: "theme", keys: ["theme"], scope: "setup.config", store: "record", op: "saveMessageBoard" },
  { field: "queuer display options", keys: ["nameFormat", "showWaitEstimates"], scope: "setup.config", store: "record", op: "saveMessageBoard" },
  { field: "welcome message", keys: ["message"], scope: "setup.config", store: "record", op: "saveMessageBoard" },
  { field: "fixed content blocks", keys: ["blocks", "layout"], scope: "setup.config", store: "record", op: "saveMessageBoard", note: "layout stays derived from the blocks for back-compat consumers" },
  { field: "board configuration, before the board is saved", keys: ["boardCfg"], scope: "setup.*", store: "doc", op: "putJourneyDocument boardCfg{} (debounced)" },
];


/* ── Appointment field ownership (2026-08-31) ────────────────────────
 * The type with no record. Every row is either the DOCUMENT (the funnel's own
 * presentation and selection) or the CATALOGUE (tenant-wide truth, referenced
 * and never copied). Nothing here writes an appointment record, because none
 * exists — that is the ask in
 * CD-API-REQUEST-service-scoped-attachments-2026-08-31.md §8. */
export const APPOINTMENT_FIELD_OWNERSHIP: FieldOwnership[] = [
  { field: "ordered service selection", keys: ["apptSetup.serviceSelection"], scope: "setup.services", store: "doc", op: "putJourneyDocument" },
  { field: "group services by category", keys: ["apptSetup.groupByCategory"], scope: "setup.services", store: "doc", op: "putJourneyDocument" },
  { field: "service → line-of-business membership", keys: ["service.lob"], scope: "setup.services", store: "record", op: "updateCatalogueService", note: "TENANT-WIDE: moving a service changes every surface that reads the catalogue" },
  { field: "duration, buffers, price", keys: ["service.durationMin", "service.preMin", "service.postMin"], scope: "setup.services", store: "record", op: "read-only · updateCatalogueService", note: "referenced with a jump; never edited from inside one funnel" },
  { field: "locations offered", keys: ["apptSetup.locationIds"], scope: "setup.places", store: "doc", op: "putJourneyDocument" },
  { field: "methods exposed", keys: ["apptSetup.methods"], scope: "setup.places", store: "doc", op: "putJourneyDocument", note: "ceilinged by each service's own channels" },
  { field: "booking + cancellation windows", keys: ["service.minAdvance", "service.maxAdvance", "service.minCancel", "service.disableBefore", "service.disableAfter"], scope: "setup.window", store: "record", op: "read-only · updateCatalogueService" },
  { field: "booking questions per service", keys: ["service.questionGroups.appointments"], scope: "setup.perservice", store: "record", op: "read-only · updateCatalogueService" },
  { field: "survey / promo / flow per service", keys: ["service.surveys", "service.promos", "service.flows"], scope: "setup.perservice", store: "record", op: "BLOCKED — CD-API-REQUEST-service-scoped-attachments-2026-08-31" },
  { field: "standard fields + consent + terms", keys: ["apptSetup.standardFields", "apptSetup.consent", "apptSetup.termsCopy", "apptSetup.policyUrl"], scope: "setup.details", store: "doc", op: "putJourneyDocument" },
  { field: "post-booking redirect", keys: ["apptSetup.redirectUrl"], scope: "setup.details", store: "doc", op: "putJourneyDocument" },
  { field: "fallback message flow", keys: ["apptSetup.messagingFlowId"], scope: "setup.messaging", store: "doc", op: "putJourneyDocument" },
  { field: "legacy journey-wide survey / promo", keys: ["apptSetup.legacySurveyId", "apptSetup.legacyPromoId"], scope: "setup.perservice", store: "doc", op: "putJourneyDocument (remove only)", note: "read-only rows; settable only to null, and gone for good once removed" },
  { field: "publishing settings", keys: ["publish.slug", "publish.tenantHost", "publish.seoTitle", "publish.shareImage"], scope: "publish", store: "doc", op: "putJourneyDocument" },
  { field: "pages, blocks and flow", keys: ["pages", "flow"], scope: "page", store: "doc", op: "putJourneyDocument" },
];

export const APPOINTMENT_BINDING: JourneyBinding = {
  type: "Appointment",
  ownsRecord: false, // no journey-bearing record exists for a LOB funnel — confirmed correct by Claude Code 2026-08-22; blocked on the availability contract (CD-API-REQUEST-journey-availability-2026-08-14)
  deriveDocFn: "docFromApptGroup",
};

export const EXPERIENCE_BINDING: JourneyBinding = {
  type: "Experience",
  ownsRecord: false, // by design — an anchor journey plus attached journeys by reference
};

export const JOURNEY_BINDINGS: Record<string, JourneyBinding> = {
  Queue: QUEUE_BINDING,
  Event: EVENT_BINDING,
  Appointment: APPOINTMENT_BINDING,
  Experience: EXPERIENCE_BINDING,
  Board: BOARD_BINDING,
};

export function bindingFor(doc: Doc): JourneyBinding | undefined {
  return doc.type ? JOURNEY_BINDINGS[doc.type] : undefined;
}

/* ── Stage 2 — read + readiness ───────────────────────────────────────
 * The record half of an `event:` journey: fetch the real event, derive its
 * document, and compute the SAME readiness rows the wizard's Publish step
 * gates on — one rule set, read from requiredEventChecks() rather than
 * re-implemented here, so the rail's dots and the designer's Publish view
 * can never drift from what actually blocks a real publish.
 */

/** requiredEventChecks() reads a wizard-shaped config; EventLike carries the
 *  same field names once fetched live, so this is a rename, not a remap. */
function eventLikeToChecksInput(e: EventLike): Record<string, unknown> {
  return {
    title: e.title, description: e.description, method: e.method, visibility: e.visibility,
    location: e.location, locationId: e.locationId, venueName: e.venueName, address: e.address,
    virtualProvider: e.virtualProvider, start: e.start, end: e.end, timezone: e.timezone, allDay: e.allDay,
    ticketTypeIds: e.ticketTypeIds, ticketGroupIds: e.ticketGroupIds, tickets: e.tickets,
  };
}

export function eventReadiness(e: EventLike, opts?: { template?: boolean }): RequiredCheck[] {
  return requiredEventChecks(eventLikeToChecksInput(e), opts);
}

/** Raw StudioEventDto → EventLike. Field names not yet visible on a derived-
 *  list row (format, locationName/locationAddress/locationId, structured
 *  `location`) are read with the same camelCase-then-snake_case tolerance
 *  `CreateEventPublish.tsx`'s loadEvent hydration already uses for this exact
 *  DTO — not a new assumption, the established one, kept in one place. */
/** A stored instant -> the DatePicker's value shape: ISO 8601 carrying the offset of
 *  the event's zone ("2026-11-02T10:00:00-05:00"). The picker reads the literal wall
 *  clock, so a raw "...Z" instant would show UTC time. Without a zone, or a value that
 *  does not parse, the value is passed through unchanged. */
function zonedIso(value: unknown, tz: string | undefined): string | undefined {
  if (value == null || value === "") return undefined;
  const raw = String(value);
  const ms = Date.parse(raw);
  if (!tz || Number.isNaN(ms)) return raw;
  const p = toParts(ms, tz);
  if (!p) return raw;
  const off = Math.round((Date.UTC(p.y, p.mo, p.d, p.h, p.mi) - (ms - (ms % 60000))) / 60000);
  const pad = (n: number) => String(n).padStart(2, "0");
  const a = Math.abs(off);
  return `${p.y}-${pad(p.mo + 1)}-${pad(p.d)}T${pad(p.h)}:${pad(p.mi)}:00${off < 0 ? "-" : "+"}${pad(Math.floor(a / 60))}:${pad(a % 60)}`;
}

/* The record holds the canonical visibility ("public" / "private"); the designer's
   picker offers its own option values, which the backend normalises back on save. Map the
   canonical value onto the matching option so an existing event opens with its visibility
   chosen (CC, 2026-10-06). An option value already stored passes through unchanged. */
function visibilityOption(v: unknown): string | undefined {
  if (v == null) return undefined;
  const raw = String(v).trim();
  if (raw === "public") return "Public · Anyone with link";
  if (raw === "private") return "Private · Internal";
  return raw || undefined;
}

/* The record's per-type allocation ({ <typeId>: { qty, kind, price } }), camelCase or
   snake_case, an object or its JSON text. Without it an event opened with every type
   at 0 allocated, and a tickets save would have written those zeros back (CC, 2026-10-06). */
function readTicketTypeConfigs(v: unknown): EventLike["ticketTypeConfigs"] {
  let o: unknown = v;
  if (typeof o === "string") { try { o = JSON.parse(o); } catch { return undefined; } }
  if (!o || typeof o !== "object" || Array.isArray(o)) return undefined;
  const out: NonNullable<EventLike["ticketTypeConfigs"]> = {};
  for (const [k, c] of Object.entries(o as Record<string, unknown>)) {
    if (!c || typeof c !== "object") continue;
    const r = c as { qty?: unknown; kind?: unknown; price?: unknown };
    out[k] = {
      ...(r.qty != null && Number.isFinite(Number(r.qty)) ? { qty: Number(r.qty) } : {}),
      ...(typeof r.kind === "string" ? { kind: r.kind } : {}),
      ...(r.price != null && Number.isFinite(Number(r.price)) ? { price: Number(r.price) } : {}),
    };
  }
  return out;
}

/* A list of strings stored as an array or as its JSON text. Anything else reads as unset. */
function readStringList(v: unknown): string[] | undefined {
  let o: unknown = v;
  if (typeof o === "string") { try { o = JSON.parse(o); } catch { return undefined; } }
  return Array.isArray(o) ? o.filter((x) => typeof x === "string" && x.trim()).map(String) : undefined;
}

/* The event's stored messaging configuration (MessagingConfig), from whichever name the
   record carries it under, an object or its JSON text. `{}` when it carries none. */
function readMessagingConfig(raw: Record<string, unknown>): Record<string, unknown> {
  for (const v of [raw.messaging, raw.messagingConfig, raw.messaging_config, raw.messaging_config_json]) {
    let o: unknown = v;
    if (typeof o === "string") { try { o = JSON.parse(o); } catch { continue; } }
    if (o && typeof o === "object" && !Array.isArray(o)) return { ...(o as Record<string, unknown>) };
  }
  return {};
}

/** The stored event `format` spelling is "in-person" (the events backend's default and every
 *  seeded row); "in_person" is the older designer spelling. Compared without the separator, so
 *  re-choosing the format an event already has is a no-op, not a change (CC, 2026-10-06). */
export function sameEventFormat(a: unknown, b: unknown): boolean {
  const n = (v: unknown) => (v == null ? "" : String(v).trim().toLowerCase().replace(/[\s_-]+/g, ""));
  return n(a) === n(b);
}

/** An all-day event ends at the end of its last day, in the event's zone: 23:59:59 on the date
 *  `value` names, with that zone's offset. A single-day all-day event sent start == end at
 *  midnight, which the worker refuses (422 EVENT_END_BEFORE_START). `value` is the picker's wall
 *  clock (its date part is the day); anything without a date passes through unchanged. */
/** The same wall clock one calendar day later in the event's zone, as ISO 8601 with that zone's offset. A
 *  calendar day, never +24 hours, so a clock change overnight keeps the clock. A value that does not parse,
 *  or no usable zone, passes through unchanged.
 *
 *  Turning that wall clock into an instant follows the "compatible" rule (as Temporal does):
 *  - a clock inside a spring-forward gap does not exist, so it moves FORWARD by the gap: 02:30 on the day
 *    New York's clocks go forward is sent as 03:30 EDT, never as 01:30 EST (which would end before it began);
 *  - a clock that happens twice, in the fall-back hour, takes the EARLIER instant: 01:30 EDT, not 01:30 EST.
 *  The result is written from the instant chosen, so its clock and offset always agree. */
export function nextDayEnd(value: string | undefined, tz: string | undefined): string | undefined {
  if (!value) return value;
  const ms = Date.parse(String(value));
  const zone = tz ? tz.replace(/^\(GMT[+-]?\d{1,2}(:\d{2})?\)\s*/, "") : undefined;
  if (Number.isNaN(ms) || !zone) return value;
  const p = toParts(ms, zone);
  if (!p) return value;
  const wall = Date.UTC(p.y, p.mo, p.d + 1, p.h, p.mi);
  const offsetAt = (at: number): number | null => {
    const q = toParts(at, zone);
    return q ? Math.round((Date.UTC(q.y, q.mo, q.d, q.h, q.mi) - (at - (at % 60000))) / 60000) : null;
  };
  // The zone's offsets either side of any change near this clock (no zone changes twice within a day).
  const before = offsetAt(wall - 86_400_000);
  const after = offsetAt(wall + 86_400_000);
  if (before == null || after == null) return value;
  const clockAt = (at: number): number | null => {
    const q = toParts(at, zone);
    return q ? Date.UTC(q.y, q.mo, q.d, q.h, q.mi) : null;
  };
  // Every instant whose local clock reads exactly this wall clock: one normally, two in a fall-back hour,
  // none in a spring-forward gap.
  const exact = [...new Set([before, after])].map((o) => wall - o * 60000).filter((t) => clockAt(t) === wall).sort((x, y) => x - y);
  // A gap: the earlier offset puts the instant just past the change, i.e. the clock moved forward by the gap.
  const at = exact.length ? exact[0]! : wall - before * 60000;
  const q = toParts(at, zone);
  const off = offsetAt(at);
  if (!q || off == null) return value;
  const pad = (n: number) => String(n).padStart(2, "0");
  const a = Math.abs(off);
  return `${q.y}-${pad(q.mo + 1)}-${pad(q.d)}T${pad(q.h)}:${pad(q.mi)}:00${off < 0 ? "-" : "+"}${pad(Math.floor(a / 60))}:${pad(a % 60)}`;
}

/** The end the event routes accept for a timed schedule. They refuse an end at or before the start, so a
 *  same-date end earlier than the start (scheduleEndState "next-day") is sent as the next morning; any other
 *  end is sent as it is. */
export function sendableEnd(start: string | undefined, end: string | undefined, tz: string | undefined): string | undefined {
  if (!end) return end;
  return scheduleEndState(start, end, { timeZone: tz }) === "next-day" ? nextDayEnd(end, tz) : end;
}

export function allDayEnd(value: string | undefined, tz: string | undefined): string | undefined {
  if (!value) return value;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
  if (!m) return value;
  const zone = tz ? tz.replace(/^\(GMT[+-]?\d{1,2}(:\d{2})?\)\s*/, "") : undefined;
  const wall = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 23, 59, 59);
  const offsetAt = (ms: number): number | null => {
    const p = toParts(ms, zone);
    return p ? Math.round((Date.UTC(p.y, p.mo, p.d, p.h, p.mi) - (ms - (ms % 60000))) / 60000) : null;
  };
  const first = offsetAt(wall);
  if (first == null) return value;
  const off = offsetAt(wall - first * 60000) ?? first;
  const pad = (n: number) => String(n).padStart(2, "0");
  const a = Math.abs(off);
  return `${m[1]}-${m[2]}-${m[3]}T23:59:59${off < 0 ? "-" : "+"}${pad(Math.floor(a / 60))}:${pad(a % 60)}`;
}

/** The Setup keys the event worker refuses to change while the event is live (403
 *  EVENT_LIVE_LOCKED), in the designer's own key names. Attendees registered against these, so
 *  the designer shows them read-only on a live event rather than letting a save be refused.
 *  Editable while live (the events worker's rule, 2026-10-06): the description, the cover
 *  image, the join link, tags, category, the waitlist settings, hold categories, the host and a
 *  capacity INCREASE (capacity is checked on its own). The one list to change. */
export const EVENT_LIVE_LOCKED_KEYS = new Set([
  "title", "method", "format",
  "location", "locationId", "locationName", "locationAddress", "venueName", "address", "locationMode",
  "start", "end", "timezone", "allDay", "repeatIndex", "customDates", "dayHours",
  "registrationOpens", "registrationCloses",
  "ticketTypeIds", "ticketTypeConfigs", "tickets", "coupons", "bookingQuestions",
  "approvalMode", "maxGuests", "allowGuests", "consent", "termsCopy", "redirectUrl", "standardFields",
]);

/** A live event's capacity may only grow (or be lifted to unlimited, 0). Lowering it, or capping
 *  an unlimited event, is refused like any locked field. */
export function liveCapacityAllowed(before: number | null | undefined, next: number | null | undefined): boolean {
  const b = Number(before ?? 0) || 0;
  const n = Number(next ?? 0) || 0;
  if (n === 0) return true;
  return b !== 0 && n >= b;
}

/** Exported for the location-mode test (CC 0396). */
export function rawToEventLike(id: string, raw: Record<string, unknown>): EventLike {
  const s = (v: unknown): string | undefined => (v == null ? undefined : String(v));
  /* The record stores format as "in-person" (the events backend's own default and the
     seeded rows) as well as "in_person"; read both, or an in-person event opens with no
     format and no venue field (CC, 2026-10-06). */
  const format = s(raw.format)?.trim().toLowerCase().replace(/[\s-]+/g, "_");
  const method = format === "hybrid" ? { inPerson: true, virtual: true }
    : format === "virtual" || format === "online" ? { inPerson: false, virtual: true }
    : format === "in_person" ? { inPerson: true, virtual: false }
    : undefined;
  const venueName = s(raw.locationName ?? (raw as Record<string, unknown>).location_name);
  const address = s(raw.locationAddress ?? (raw as Record<string, unknown>).location_address);
  // raw.location is the STRUCTURED validated-address object on a real DTO
  // (store.ts's `loadEvent` hydration), never a display string — never
  // stringify it directly.
  const locationDisplay = venueName || address || (typeof raw.location === "string" ? raw.location : undefined);
  return {
    id, title: s(raw.title) ?? "", status: s(raw.status) ?? "draft",
    description: s(raw.description), location: locationDisplay,
    venueName, address,
    locationId: s(raw.locationId ?? (raw as Record<string, unknown>).location_id),
    /* Derived, as creation-store says it should be (CC 0396): a typed venue or address with no saved
       location opens in address mode; everything else opens on the saved-location select. */
    locationMode: !s(raw.locationId ?? (raw as Record<string, unknown>).location_id) && (venueName || address) ? "custom" : "saved",
    /* The record names (StudioEventDto): meetingLink, imageUrl, startsAt/endsAt (UTC
       instants), timezone. Setup's own keys stay virtualProvider/heroImage/start/end;
       the dates are shown as the event's own wall clock, which the picker reads literally. */
    method, visibility: visibilityOption(raw.visibility), virtualProvider: s(raw.meetingLink ?? raw.meeting_link ?? raw.virtualProvider),
    heroImage: s(raw.imageUrl ?? raw.image_url ?? raw.heroImage),
    timezone: s(raw.timezone),
    start: zonedIso(raw.startsAt ?? raw.start_at ?? raw.start, s(raw.timezone)),
    end: zonedIso(raw.endsAt ?? raw.end_at ?? raw.end, s(raw.timezone)),
    ticketTypeIds: (raw.ticketTypeIds as string[] | undefined) ?? undefined,
    ticketTypeConfigs: readTicketTypeConfigs(raw.ticketTypeConfigs ?? raw.ticket_type_configs),
    ticketGroupIds: (raw.ticketGroupIds as string[] | undefined) ?? undefined,
    tickets: (raw.tickets as unknown[] | undefined) ?? undefined,
    isPaid: !!raw.isPaid, capacity: (raw.capacity as number | null | undefined) ?? null,
    /* Waitlist and approval are on the record as allowWaitlist (allow_waitlist 0/1) and
       approvalMode "auto" / "manual"; Setup keeps them as booleans. */
    waitlist: raw.allowWaitlist != null ? raw.allowWaitlist === true || raw.allowWaitlist === 1
      : raw.allow_waitlist != null ? raw.allow_waitlist === true || raw.allow_waitlist === 1 || raw.allow_waitlist === "1" : undefined,
    approvalMode: raw.approvalMode != null ? raw.approvalMode === true || raw.approvalMode === "manual" : undefined,
    bookingQuestions: (raw.bookingQuestions as EventLike["bookingQuestions"]) ?? undefined,
    surveys: (raw.surveys as string[] | undefined) ?? undefined,
    /* The record's tags (an array, or the stored tags_json). Without the read an author
       adding one tag sent [that tag] and wiped the rest (CC, 2026-10-06). */
    tags: readStringList(raw.tags ?? raw.tags_json),
    /* The message flow the event runs, as the record names it (messagingFlowId on the
       record or inside its stored messaging config). */
    messagingFlowId: s(raw.messagingFlowId ?? raw.messaging_flow_id ?? readMessagingConfig(raw).messagingFlowId),
    // JAI-1767: the Setup pane's picker writes this; without the read back an
    // author sees an empty control over a stored list and silently overwrites
    // it. Same normalizer the queue pane uses, so an unknown category is
    // dropped rather than round-tripped.
    resolvesHolds: readResolvesHolds(raw.resolvesHolds),
    // JAI-2020 — read through untouched; nothing writes it until the kit lands.
    audience: (raw as { audience?: unknown }).audience ?? null,
  };
}

/** The event's public slug and address, from its share record (getEventShare, CC 0408): the event read
 *  carries neither. publicUrl is kept only when absolute https; a relative one ("/<slug>") leaves the slug for
 *  the dialog to build from. A failed read returns nothing, and the dialog builds from the title as before. */
export async function readEventShareAddress(eventId: string, read: (id: string) => Promise<{ slug?: string | null; publicUrl?: string | null }> = getEventShare): Promise<{ slug?: string; publicUrl?: string }> {
  try {
    const share = await read(eventId);
    const slug = typeof share?.slug === "string" && share.slug.trim() ? share.slug.trim() : undefined;
    const url = typeof share?.publicUrl === "string" ? share.publicUrl.trim() : "";
    return { ...(slug ? { slug } : {}), ...(/^https:\/\//i.test(url) ? { publicUrl: url } : {}) };
  } catch {
    return {};
  }
}

/** Put an event's loaded share address on its document's origin (CC 0411). The list-derived document
 *  carries no slug or publicUrl, and the dialog reads origin, so the loaded value must land there; when
 *  both exist the loaded share value wins. Documents that are not events, or have nothing loaded, are
 *  returned unchanged (same object). */
export function withEventAddress(doc: Doc, loaded: { slug?: string | null; publicUrl?: string | null } | undefined): Doc {
  const o = doc?.origin;
  if (!o || o.kind !== "event" || !loaded) return doc;
  const slug = typeof loaded.slug === "string" && loaded.slug ? loaded.slug : null;
  const publicUrl = typeof loaded.publicUrl === "string" && /^https:\/\//i.test(loaded.publicUrl) ? loaded.publicUrl : null;
  if (!slug && !publicUrl) return doc;
  if ((!slug || o.slug === slug) && (!publicUrl || o.publicUrl === publicUrl)) return doc;
  return { ...doc, origin: { ...o, ...(slug ? { slug } : {}), ...(publicUrl ? { publicUrl } : {}) } };
}

/** Fetches the real event, derives its document, and returns readiness
 *  alongside it. `getStudioEvent` is the same read op the wizard's edit-open
 *  hydration uses — no new contract, per the stage-2 risk check. */
export async function loadEventDoc(eventId: string): Promise<{ key: string; doc: Doc; readiness: RequiredCheck[]; record: EventLike } | null> {
  const [raw, address] = await Promise.all([
    getStudioEvent(eventId, EV_OPTS) as unknown as Promise<Record<string, unknown>>,
    readEventShareAddress(eventId),
  ]);
  if (!raw) return null;
  const like = { ...rawToEventLike(eventId, raw), ...address };
  const { key, doc } = docFromEvent(like);
  return { key, doc, readiness: eventReadiness(like), record: like };
}

/* ── Stage 3 — Setup writes ───────────────────────────────────────────
 * One function per setup.* scope in EVENT_FIELD_OWNERSHIP, each calling the
 * op that scope's rows name. Guests has two: an add/import writes through
 * updateEventGuestList, so both funnel through the same call here.
 */

/**
 * A WRITE that landed must not be reported as a failure because the RESPONSE
 * could not be parsed (2026-09-17, PP).
 *
 * `creation.updateEvent` and its siblings funnel through `mutateEvent`, which
 * ends in `StudioEventDto.parse(...)`. That DTO requires the worker's
 * snake_case row — `tenant_id`, `starts_at`, `ends_at`, `created_at`,
 * `updated_at` — and the design-time serving layer answers a 200 carrying the
 * camelCase event instead, with none of those five. So every event Setup write
 * in this environment resolved as a rejection AFTER the server had already
 * accepted it.
 *
 * It went unnoticed for as long as it did because these writes fired on blur
 * with no success signal and no failure signal; the explicit Save added today
 * is what made it visible. See the drop note — the mock's PATCH response is the
 * thing to fix.
 *
 * The distinction this makes is narrow on purpose: a `ZodError` means the
 * server answered 2xx and only the client's model of the answer was wrong, so
 * the save stands and we log. Anything else — a network failure, a 4xx/5xx
 * from `apiRaw` — still rejects and still surfaces to the author.
 */
async function writeEvent(run: () => Promise<unknown>, op: string): Promise<void> {
  /* TIGHTENED 2026-10-01 (CC, Greg's question a): a response that fails the contract
     parse is a FAILED save, not "saved". The lenient path is what hid the
     create-payload bug. The author sees the failure; the console names the op. */
  try {
    await run();
  } catch (err) {
    const name = (err as { name?: string } | null)?.name;
    const isSchema = name === "ZodError" || Array.isArray((err as { issues?: unknown[] } | null)?.issues);
    if (isSchema) {
      console.warn(`[nj-binding] ${op}: the response did not match StudioEventDto, so the save is reported as failed:`, err);
      throw new Error("The server's answer didn't match the expected event shape, so this save can't be confirmed.");
    }
    throw err;
  }
  /* Saved: re-read the events list so it shows this change without a reload. */
  void refetchEvents();
}

/** Setup's keys -> the names the event PATCH reads (EventMutationSchema in the events
 *  worker). The worker drops keys it does not declare, so `start`, `end`,
 *  `virtualProvider` and `heroImage` were accepted and silently lost. An empty date is
 *  not sent: the worker refuses a blank startsAt/endsAt, and a date cannot be cleared. */
export function toEventWire(patch: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = { ...patch };
  const rename = (from: string, to: string, keepEmpty: boolean) => {
    if (!(from in out)) return;
    const v = out[from];
    delete out[from];
    if (to in out) return;
    if (keepEmpty || (v != null && v !== "")) out[to] = v == null ? "" : v;
  };
  rename("start", "startsAt", false);
  rename("end", "endsAt", false);
  rename("virtualProvider", "meetingLink", true);
  rename("heroImage", "imageUrl", true);
  if (typeof out.timezone === "string") out.timezone = out.timezone.replace(/^\(GMT[+-]?\d{1,2}(:\d{2})?\)\s*/, "");
  return out;
}

/** True when the event worker refused a change because the event is live (403
 *  EVENT_LIVE_LOCKED): its dates, times and other core fields cannot change. */
export function isEventLiveLocked(err: unknown): boolean {
  return (err as { code?: unknown } | null)?.code === "EVENT_LIVE_LOCKED";
}

export async function patchEventBasics(id: string, patch: Record<string, unknown>): Promise<void> {
  await writeEvent(() => creation.updateEvent(id, toEventWire(patch) as unknown as Parameters<typeof creation.updateEvent>[1]), "patchEventBasics");
}
export async function patchEventDetails(id: string, patch: Record<string, unknown>): Promise<void> {
  await writeEvent(() => creation.updateEvent(id, toEventWire(patch) as unknown as Parameters<typeof creation.updateEvent>[1]), "patchEventDetails");
}
export async function patchEventSchedule(id: string, patch: Record<string, unknown>): Promise<void> {
  await writeEvent(() => creation.updateEvent(id, toEventWire(patch) as unknown as Parameters<typeof creation.updateEvent>[1]), "patchEventSchedule");
}
/* Ticket-type edits (ticketTypeIds / ticketTypeConfigs / capacity / waitlist / coupons)
   go through the normal event update (CC, Greg's question b): the deployed
   PATCH /v1/admin/events/{id}/tickets requires model + tickets[] and refuses anything
   else with 400. updateEventTickets is kept only for that full shape. */
export async function patchEventTickets(id: string, patch: Record<string, unknown>): Promise<void> {
  const full = typeof patch.model === "string" && Array.isArray(patch.tickets);
  if (full) {
    await writeEvent(() => creation.updateEventTickets(id, patch as unknown as Parameters<typeof creation.updateEventTickets>[1]), "patchEventTickets");
    return;
  }
  await writeEvent(() => creation.updateEvent(id, patch as unknown as Parameters<typeof creation.updateEvent>[1]), "patchEventTickets");
}
/** Ticket GROUPS attach through their own op per the ownership map — groups are
 *  library objects referenced by id, never copied onto the event. */
/** @deprecated CC 0195: the designer applies groups through Tickets v2 (nj-tickets-v2.ts). Kept only for any other caller. */
export async function attachEventTicketGroupIds(id: string, groupIds: string[]): Promise<void> {
  await writeEvent(() => creation.attachEventTicketGroups(id, { ticketGroupIds: groupIds } as unknown as Parameters<typeof creation.attachEventTicketGroups>[1]), "attachEventTicketGroupIds");
}
/** The designer's registration keys → the names PATCH /v1/admin/events/{id}/registration
 *  reads (EventRegistrationConfig; CC 0129, JAI-2119). The screen's record keeps its own
 *  shape; only the wire body is renamed. A save carries only what it changed (the
 *  endpoint writes partial bodies). `registrationOpens` / `registrationCloses` are
 *  NOT sent (CC 0137): their dropdowns are hidden until CC's relative window fields land,
 *  and they must not be turned into instants here. */
/** The keys EventRegistrationConfig declares (creation.ts), kept in step with it. */
const REGISTRATION_KEYS = new Set(["registrationModel", "regOpens", "regCloses", "approvalMode", "allowGuests", "maxGuests", "requireGuestDetails", "guestDetailsMode", "confirmAction", "redirectUrl", "termsText", "collectMarketing", "collectShareConsent", "stdFields", "questionConfig", "questionCount", "promoConfig"]);
export function toRegistrationBody(patch: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(patch)) {
    switch (k) {
      case "termsCopy": out.termsText = typeof v === "string" ? v : ""; break;
      case "redirectUrl": {
        const url = typeof v === "string" ? v.trim() : "";
        out.redirectUrl = url;
        // Without confirmAction the stored action stays on the confirmation page.
        out.confirmAction = url ? "redirect" : "confirmation";
        break;
      }
      case "approvalMode": out.approvalMode = v === true || v === "manual" ? "manual" : "auto"; break;
      case "consent": {
        const c = (v ?? {}) as { marketing?: unknown; contactSharing?: unknown };
        if (c.marketing !== undefined) out.collectMarketing = c.marketing === true;
        if (c.contactSharing !== undefined) out.collectShareConsent = c.contactSharing === true;
        break;
      }
      case "standardFields": {
        // First and last name are always collected; the screen toggles email and phone.
        const on = Array.isArray(v) ? v.map(String) : [];
        out.stdFields = { email: on.includes("email") ? "required" : "off", phone: on.includes("phone") ? "optional" : "off" };
        break;
      }
      case "maxGuests": out.maxGuests = Math.max(0, Math.trunc(Number(v) || 0)); break;
      /* Only keys EventRegistrationConfig declares go on the wire (CC 0134 §5: the request
         is strict, an unknown key is a 422). The two window keys are never sent (CC 0137):
         the dropdowns are hidden until the relative window fields exist. */
      case "registrationOpens": case "registrationCloses": break;
      default: if (REGISTRATION_KEYS.has(k)) out[k] = v;
    }
  }
  return out;
}
export async function patchEventRegistration(id: string, patch: Record<string, unknown>): Promise<void> {
  const body = toRegistrationBody(patch);
  await writeEvent(() => creation.updateEventRegistration(id, body as unknown as Parameters<typeof creation.updateEventRegistration>[1]), "patchEventRegistration");
}
/** One designer guest -> the GuestListEntry the guest-list route stores (UpdateEventGuestListInput:
 *  `{ entries }`, each with a uid, a kind and a name). A person typed or found in the directory is
 *  a `new` entry; a segment carries its library id and size. */
export function toGuestListEntry(g: GuestEntry): Record<string, unknown> {
  const first = String(g.firstName ?? "").trim();
  const last = String(g.lastName ?? "").trim();
  const email = String(g.email ?? "").trim();
  if (g.kind === "segment") {
    const segId = String(g.id ?? first);
    return { uid: g.uid ?? `segment-${segId}`, kind: "segment", id: segId, name: first, count: Math.max(0, Math.trunc(Number(g.count) || 0)) };
  }
  return {
    uid: g.uid ?? `guest-${(email || `${first} ${last}`).toLowerCase().replace(/\s+/g, "-")}`,
    kind: "new", firstName: first, ...(last ? { lastName: last } : {}), name: `${first} ${last}`.trim(),
    ...(email ? { email } : {}),
  };
}
/* The route replaces the whole list with `entries` (it never read `guestList`, so the old
   body was refused, CC 2026-10-06). Takes the held `{ entries }` patch or the list itself. */
export async function patchEventGuestList(id: string, patch: { entries?: GuestEntry[] } | GuestEntry[]): Promise<void> {
  const guests = Array.isArray(patch) ? patch : (patch.entries ?? []);
  const body = { entries: guests.map(toGuestListEntry) };
  await writeEvent(() => creation.updateEventGuestList(id, body as unknown as Parameters<typeof creation.updateEventGuestList>[1]), "patchEventGuestList");
}

/* PATCH /v1/admin/events/{id}/messaging REPLACES the stored configuration with the body, so
   a save that carried only the flow (or only the surveys) dropped everything else. The stored
   configuration is read first and the change is merged onto it; a read that fails stops the
   save rather than overwrite what it could not see. The flow is `messagingFlowId` (the
   MessagingConfig key); a cleared flow is left out, which is how the route stores "none". */
export async function patchEventMessaging(id: string, patch: Record<string, unknown>): Promise<void> {
  const raw = await getStudioEvent(id, EV_OPTS) as unknown as Record<string, unknown>;
  const stored = readMessagingConfig(raw ?? {});
  const storedFlow = raw?.messagingFlowId ?? raw?.messaging_flow_id;
  if (stored.messagingFlowId == null && typeof storedFlow === "string" && storedFlow) stored.messagingFlowId = storedFlow;
  const change: Record<string, unknown> = { ...patch };
  if ("flowId" in change) { if (!("messagingFlowId" in change)) change.messagingFlowId = change.flowId; delete change.flowId; }
  const body: Record<string, unknown> = { ...stored, ...change };
  if (body.messagingFlowId == null || body.messagingFlowId === "") delete body.messagingFlowId;
  await writeEvent(() => creation.updateEventMessaging(id, body as unknown as Parameters<typeof creation.updateEventMessaging>[1]), "patchEventMessaging");
}

/* ── Stage 5 — publish, offline, cancel ───────────────────────────────
 * CORRECTED 2026-08-22 (Claude Code, answering the newjourneys API request):
 * publishing is a first-class operation, not a status write. `publishEvent`
 * and `takeEventOffline` are FLAT exports of @v0y4ge/api-client (not on the
 * `creation` namespace) and hit their own routes — POST /v1/landing/{id}/publish
 * and POST /v1/admin/events/{id}/take-offline. The earlier reading of
 * CreateEventPublish.tsx's `status: "live"` payload was the wrong path: a
 * status write diverges from what the backend actually does on publish.
 * Cancel and discard remain distinct real ops on the namespace.
 */
export async function publishEventRecord(id: string): Promise<void> {
  await publishEvent(id);
}
export async function takeEventRecordOffline(id: string): Promise<void> {
  await takeEventOffline(id);
}
export async function cancelEventRecord(id: string): Promise<void> {
  await creation.cancelEvent(id as unknown as Parameters<typeof creation.cancelEvent>[0]);
}

/* ── Queue setup writes (queue editor port, 2026-08-24) ───────────────
 * The queue editor autosaved every field through updateQueue and published
 * through setQueueStatus. The designer's Setup views call these, so the two
 * surfaces cannot drift: same ops, same payload shape.
 */
export async function patchQueueConfig(id: string, patch: Partial<QueueConfigLike>): Promise<void> {
  await updateQueue(id, patch as unknown as Parameters<typeof updateQueue>[1]);
}
export async function setQueueLifecycle(id: string, status: QueueStatus): Promise<void> {
  await setQueueStatus(id, status);
}

/** One readiness row set for a queue — the wizard's Review-step checklist,
 *  verbatim: name, location, a seeded journey, ≥1 opening-hours set, ≥1
 *  service. `tab` is the Setup view that fills the row. */
export interface SetupCheck { label: string; done: boolean; tab: string }
export function queueReadiness(q: Partial<QueueConfigLike>, _pageCount: number): SetupCheck[] {
  const hours = q.hours ?? [];
  return [
    /* Tab renamed `qtype` → `qsetup` (operator, 2026-09-02): the page is Setup
       now, because the starter picker it was named after is gone. The queue's
       pages are seeded on creation, so there is nothing on that page to choose.

       "Journey — pages seeded" retired with it. It asserted `pageCount > 0` on a
       journey whose pages are created by the template — a check that could only
       fail if the seed itself was broken, and one an author could not act on. The
       `_pageCount` argument stays so every caller does not need editing; it is
       unread on purpose. */
    { label: "Queue name", done: !!(q.name ?? "").trim(), tab: "qsetup" },
    { label: "Location", done: !!q.locationId, tab: "qsetup" },
    /* An EMPTY list is a legitimate answer, not an unfinished step: it means
       "follows location hours", which is what the label says and what the join
       door does (CD, 2026-09-17). Requiring at least one set blocked Publish
       for an author who deliberately cleared the default to inherit the
       building's hours. What is still not done is a set that names no days —
       that governs nothing and is almost certainly half-typed. */
    { label: "Opening hours", done: hours.every((h) => h.days.length > 0), tab: "qhours" },
    { label: "Services — at least one", done: (q.services ?? []).length > 0, tab: "qservices" },
    /* Staffing is REQUIRED to publish (2026-09-16, PP). It carried no check
       before, so a queue could go live with nobody assigned — the Publish
       summary said "No one assigned yet" and published anyway, and the Staffing
       tab showed no dot because there was no row that could turn one.

       An open queue with no staff takes customers into a line nobody is serving,
       which is worse than refusing to open. Adding the row here is the whole
       change: the tab dot, the Publish checklist and the Publish gate all read
       this list, so one entry gives all three at once and they cannot disagree. */
    { label: "Staffing — at least one person", done: (q.assignedStaff ?? []).length > 0, tab: "qstaffing" },
  ];
}

/* ── Display-board writes ─────────────────────────────────────────────
 * `layout` stays derived from the blocks, exactly as the retired
 * QueueBoardBuilder derived it, so legacy consumers (list column, public
 * renderer) are unaffected by boards authored in the designer.
 */
export async function saveBoardConfig(cfg: BoardConfigLike): Promise<void> {
  const layout: MessageBoard["layout"] = cfg.blocks.some((b) => b.type === "up-next") ? "full-list" : "now-serving";
  await saveMessageBoard({
    id: cfg.id, name: cfg.name.trim() || "Untitled board", queueIds: cfg.queueIds,
    locationId: cfg.locationId ?? null, layout, theme: cfg.theme,
    showWaitEstimates: cfg.showWaitEstimates, nameFormat: cfg.nameFormat,
    message: cfg.message, blocks: cfg.blocks,
  });
}
export async function deleteBoardConfig(id: string): Promise<void> {
  await deleteMessageBoard(id);
}
export function boardReadiness(b: Partial<BoardConfigLike>): SetupCheck[] {
  return [
    { label: "Board name", done: !!(b.name ?? "").trim(), tab: "boardcfg" },
    { label: "Queues shown — at least one", done: (b.queueIds ?? []).length > 0, tab: "boardcfg" },
    { label: "Content — at least one block", done: (b.blocks ?? []).length > 0, tab: "boardcfg" },
  ];
}
